import { useMemo } from 'react';
import { find, findIndex, startsWith } from 'lodash';

import { getCallback, getCallbackIndex } from '@/global';

export default function useFRCallback(FRStep) {
  const { payload = {} } = FRStep;
  const { callbacks = [] } = payload;

  // handle confirmation callback
  const confirmationCallback = useMemo(() => getCallback(callbacks, 'confirmationcallback'), [callbacks]);
  const confirmationCallbackIndex = useMemo(() => getCallbackIndex(callbacks, 'confirmationcallback'), [callbacks]);
  const confirmationCallbackOptions = useMemo(() => {
    let output;
    if (confirmationCallback) {
      const confirmationCallbackChoiceObj = find(
        confirmationCallback?.output,
        (outputObj) => outputObj?.name?.toLowerCase() === 'options'
      );

      output = confirmationCallbackChoiceObj?.value;
    }

    return output || [];
  }, [confirmationCallback]);

  // handle choice callback
  const choiceCallback = useMemo(() => getCallback(callbacks, 'choicecallback'), [callbacks]);
  const choiceCallbackIndex = useMemo(() => getCallbackIndex(callbacks, 'choicecallback'), [callbacks]);
  const choiceCallbackChoices = useMemo(() => {
    let output;
    if (choiceCallback) {
      const choiceCallbackChoiceObj = find(
        choiceCallback?.output,
        (outputObj) => outputObj?.name?.toLowerCase() === 'choices'
      );

      output = choiceCallbackChoiceObj?.value;
    }

    return output || [];
  }, [choiceCallback]);

  // handle string attribute input callback
  const stringAttributeInputCallback = useMemo(
    () => getCallback(callbacks, 'stringattributeinputcallback'),
    [callbacks]
  );
  const stringAttributeInputCallbackIndex = useMemo(
    () => getCallbackIndex(callbacks, 'stringattributeinputcallback'),
    [callbacks]
  );

  // handle suspended text output callback
  const suspendedTextOutputCallback = useMemo(() => getCallback(callbacks, 'suspendedtextoutputcallback'), [callbacks]);
  const suspendedTextOutputMessage = useMemo(() => {
    let output;
    if (suspendedTextOutputCallback) {
      const messageObj = find(
        suspendedTextOutputCallback?.output,
        (outputObj) => outputObj?.name?.toLowerCase() === 'message'
      );

      output = messageObj?.value;
    }

    // try to parse output string if needed; check for opening curly brace to avoid error on parsing a simple string
    if (output && startsWith(output, '{')) {
      try {
        const parsedString = JSON.parse(output);
        output = parsedString;
      } catch (error) {
        console.log(error);
      }
    }

    return output;
  }, [suspendedTextOutputCallback]);

  // handle text output callback
  const textOutputCallback = useMemo(() => getCallback(callbacks, 'textoutputcallback'), [callbacks]);
  const textOutputCallbackIndex = useMemo(() => getCallbackIndex(callbacks, 'textoutputcallback'), [callbacks]);

  // handle text input callback
  const textInputCallback = useMemo(() => getCallback(callbacks, 'textinputcallback'), [callbacks]);
  const textInputCallbackIndex = useMemo(() => getCallbackIndex(callbacks, 'textinputcallback'), [callbacks]);

  // handle text input callback
  const getTextInputCallbackByName = (inputName) => {
    // need to return the callback itself as well as the index for easier payload manipulation
    const result = {
      callbackIndex: '',
      callback: {},
    };

    if (callbacks) {
      for (const [index, callbackObj] of callbacks.entries()) {
        const { type, output = [] } = callbackObj;

        if (type?.toLowerCase() === 'textinputcallback') {
          // need to find the 'prompt' object in the output array
          const promptIndex = findIndex(output, (outputAttrObj) => outputAttrObj.name === 'prompt');

          // check if the prompt value matches the input name passed in, and set the callback if it's a match
          if (output[promptIndex].value === inputName) {
            result.callbackIndex = index;
            result.callback = callbackObj;
          }
        }
      }
    }

    return result;
  };

  return {
    callbacks,
    confirmationCallback,
    confirmationCallbackIndex,
    confirmationCallbackOptions,
    choiceCallback,
    choiceCallbackIndex,
    choiceCallbackChoices,
    payload,
    suspendedTextOutputCallback,
    suspendedTextOutputMessage,
    stringAttributeInputCallback,
    stringAttributeInputCallbackIndex,
    textOutputCallback,
    textOutputCallbackIndex,
    textInputCallback,
    textInputCallbackIndex,
    getTextInputCallbackByName,
  };
}
